import type {
  BrowserOptions,
  ErrorEvent as SentryErrorEvent,
  Event as SentryEvent,
} from "@sentry/react";
import {
  deepMapStrings,
  failClosedEvent,
  maskTokenPath,
  SCRUB_FAILED,
  scrubBreadcrumbText,
  scrubEventText as scrubEventValues,
  scrubFreeText,
  scrubUrl as scrubUrlU1,
} from "./pii-text";

// GDPR posture: scrub anything that looks like PII before the event leaves
// the browser. Patterns are case-insensitive; the `[-_]?` form covers
// `apiKey` / `api_key` / `api-key` variants. Over-scrubbing is preferred
// to leakage — Sentry-side data scrubbing is a fallback, not the primary line.
const PII_KEY_PATTERNS: readonly RegExp[] = [
  /email/i,
  /phone/i,
  /firstname/i,
  /lastname/i,
  /name$/i,
  /address/i,
  /street/i,
  /zip/i,
  /postal/i,
  /city/i,
  /country/i,
  /birth/i,
  /dob/i,
  /passport/i,
  /token$/i,
  /password/i,
  /secret/i,
  /authorization/i,
  /cookie/i,
  /api[-_]?key/i,
  /iban/i,
  /vat/i,
  /tax[-_]?id/i,
  /mongo[-_]?uri/i,
  // IT-04 — guest custom-field payloads, treated as PII wholesale.
  //
  // Everything above matches a KNOWN key name. A custom field is named by the
  // organiser, so no list can anticipate it: "Allergien", "Unverträglichkeit",
  // "Betreuungsbedarf" are health data and match none of the patterns above.
  // The platform already blocks the Art. 9 key NAMES it can foresee
  // (AdditionalFieldConstraints.RESERVED_KEYS), and that list is itself the
  // proof that name-matching cannot solve this: "Allergien" is not on it and
  // cannot be.
  //
  // So the subtree goes as a whole. checkin-app-backend's scrub has had
  // `/^additionalfields$/i` all along; this cockpit one did not, which is the
  // asymmetry that let custom-field values reach error monitoring from here.
  //
  // Both shapes: the nested object (`additionalFields: {…}`) and the flattened
  // keys the condition/goal surfaces build (`additionalFields.<name>`).
  /^additionalfields(\.|$)/i,
];

const SENSITIVE_HEADERS: ReadonlySet<string> = new Set([
  "authorization",
  "cookie",
  "x-session-token",
  "x-edge-secret",
]);

const SCRUB_PLACEHOLDER = "[scrubbed]";
const MAX_DEPTH = 8;

const isPiiKey = (key: string): boolean =>
  PII_KEY_PATTERNS.some((p) => p.test(key));

export function scrubPii<T>(value: T, depth = 0): T {
  if (depth > MAX_DEPTH) return value;
  if (value === null || value === undefined) return value;
  if (typeof value !== "object") return value;

  if (Array.isArray(value)) {
    return value.map((v) => scrubPii(v, depth + 1)) as unknown as T;
  }

  // Only traverse plain objects — leave Date / RegExp / class instances alone.
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return value;

  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = isPiiKey(k) ? SCRUB_PLACEHOLDER : scrubPii(v, depth + 1);
  }
  return out as unknown as T;
}

function scrubHeaders(
  headers: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (!headers) return headers;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(headers)) {
    if (SENSITIVE_HEADERS.has(k.toLowerCase())) continue;
    out[k] = v;
  }
  return out;
}

// ── canonical:start ─────────────────────────────────────────────────────────
//
// Kanzlei K4-B — values out of FREE TEXT, browser layer.
//
// The key-based scrub matches KEY names. A field value also leaves through
// free text: backend prose echoed into an exception value ("Value 'Erdnuss'
// is not a valid option"), a console line, the title of a clicked element, a
// URL. The text rules — T1–T7 + T9 and the URL rule U1 with the portal token
// mask — live in `pii-text.ts`, byte-identical with the satellites and
// main-consumer (plan squishy-honking-mountain, "Gemeinsame Textmuster").
// This block adds what only a browser produces, and is identical in
// main-frontend, website-builder-frontend and portal-frontend between the
// two `canonical:` markers. Change all or none.
//
// - T8: the attribute values Sentry copies into an element path, and the
//   element's id (`input#edit-<address>`) — bounded, no quadratic scan.
// - console crumbs: the logged objects (`data.arguments`) go whole.
// - URL fields keep no query even when relative (`/x?email=…`), a `data:`
//   URL keeps only its media type (fetch(dataUrl) turns an upload into a
//   Blob — the URL IS the content).
// - Stack frames: file names by the URL rule (an inline script's file is the
//   PAGE url — the portal's boot script reports `/p/<token>`).
// - Fail closed: a scrub that throws sends `pii-text`'s `failClosedEvent`
//   (an error: message `[scrub-failed]`, no text; a feedback stays a
//   feedback with that message; a transaction is dropped) or drops the
//   crumb — never the original, never a throw into the app.
// - No lookbehind anywhere: an older Safari refuses the whole bundle on one.
//
// What each hook covers (`sentryScrubOptions` + `sentryScrubIntegration`):
// - maxValueLength (an option, not a hook): the SDK cuts the texts BEFORE any
//   hook runs, see `SENTRY_MAX_VALUE_LENGTH`.
// - processEvent (integration) runs for EVERY event type — errors,
//   transactions, feedback: everything below at once.
// - beforeBreadcrumb: console arguments gone, ui.* element ids and attribute
//   values gone, url/from/to by the URL rule, every string through the
//   patterns.
// - beforeSend (errors): message, logentry, exception values, tags, extra,
//   contexts through `pii-text`'s free-text rules, element paths in extra /
//   contexts through T8 + the id step; request url / Referer and stack frame
//   files by the URL rule.
// - beforeSendTransaction and beforeSendSpan: names, descriptions and span
//   data through T8 + the URL rule. A standalone span (INP) passes ONLY
//   beforeSendSpan, never the other three.

const REDACTED = "[redacted]";

/**
 * The SDK cuts `message`, the first exception value and `request.url` to
 * `maxValueLength` (default 250) in prepareEvent — BEFORE processEvent and
 * every before* hook. A cut text has lost the suffix the lazy patterns anchor
 * on: `Value '<230 characters>' is not a val...` passed T6 untouched. 8192
 * keeps every realistic backend text whole (the patterns reach 2000 into a
 * value). Handed to `Sentry.init` by `sentryScrubOptions`.
 */
export const SENTRY_MAX_VALUE_LENGTH = 8192;

/**
 * T8 — the attributes Sentry copies into an element path (`htmlTreeAsString`:
 * ui.click / ui.input crumbs, INP span names, `lcp.element`, an Event's
 * target in `extra.arguments`). The value is whatever the page rendered
 * there — a guest's name on a row's aria-label. The lookahead ends a value
 * only where the next attribute, the next path step or the end follows, so a
 * value holding `"]` cannot cut it short. Bounded: a value that does not
 * close within 2000 characters takes the rest of the text with it (the
 * unbounded scan was quadratic — 100 KB took seconds).
 */
const ELEMENT_ATTRIBUTE = /\[(aria-label|title|alt|name)="(?:[\s\S]{0,2000}?"\](?=\[|\s>\s|$)|[\s\S]*$)/g;

/**
 * The id step of an element path: `tag#<id>`. An id is whatever the page set
 * — `edit-<the e-mail address>` — and may hold `.`, spaces, anything, so it
 * runs to the next attribute Sentry writes (`[type="…"]` …), the next path
 * step or the end, and the element's classes after it go with it. Bounded
 * like T8. Only in a text that starts like an element path (`tag#`, `tag.`,
 * `tag[`, `tag > `, a lone tag or component name): free text with a `#` in
 * it ("ticket #12") stays.
 */
const ELEMENT_ID =
  /(^|\s)([a-z][a-z0-9-]*)#(?:[\s\S]{0,2000}?(?=\[(?:aria-label|type|name|title|alt)[="\]]|\s>\s|$)|[\s\S]*$)/gi;
const ELEMENT_PATH_START = /^[a-z][a-z0-9-]*(?:[#.[]|\s>\s|$)/i;

/** T8 + the id step on an element path (any text: the id rule only where it starts like one). */
export function scrubSelector(value: string): string {
  const ids = ELEMENT_PATH_START.test(value) ? value.replace(ELEMENT_ID, "$1$2#[id]") : value;
  return ids.replace(ELEMENT_ATTRIBUTE, "[$1]");
}

/** U1 on one URL field: `pii-text`'s rule, plus `data:` URLs. */
export function scrubUrl(raw: string): string {
  if (/^data:/i.test(raw)) {
    return `${/^data:[^,;]*/i.exec(raw)?.[0] ?? "data:"},${REDACTED}`;
  }
  return scrubUrlU1(raw);
}

const SCRIPT_FILE = /\.(?:m?js|cjs)$/i;

/**
 * A stack frame's file (`filename`, `abs_path`, a debug image's `code_file`):
 * the URL rule with the token mask. An inline script's file is the PAGE url
 * (`https://portal…/p/<token>?lang=de`), so it gets exactly that. A script
 * FILE keeps its whole path on a foreign host too, minus query and fragment:
 * an event site or portal on a customer's domain would otherwise fold every
 * chunk to `origin/assets` and the source maps (matched by this name) stop
 * resolving. A script's path is the build's, never a value.
 */
export function scrubFrameUrl(raw: string): string {
  if (/^https?:\/\//i.test(raw)) {
    try {
      const url = new URL(raw);
      if (SCRIPT_FILE.test(url.pathname)) return `${url.protocol}//${url.host}${maskTokenPath(url.pathname)}`;
    } catch {
      // not a URL after all — the URL rule below
    }
  }
  return scrubUrl(raw);
}

/**
 * Free text as the hooks leave it: `pii-text`'s free-text rules (U1 on the
 * URLs inside, T1–T7 + T9, the portal token, e-mail, phone). Exported for
 * the pattern pins (`sentry.textPatterns.test.ts`); the hooks get it through
 * `pii-text`'s event and crumb scrub.
 */
export function scrubText(value: string): string {
  return scrubFreeText(value);
}

const METHOD_AND_URL = /^([A-Z]+) (\S+)$/;
const LONE_URL = /^(?:[a-z][a-z0-9+.-]*:|\/|\.\.?\/)\S*$/i;

/**
 * A transaction name or span description, after `pii-text`'s span rules
 * (free text, the token): an element path (INP, click spans), `METHOD url`
 * (http.client spans), a lone url or route (resource spans, pageload names),
 * or anything else. T8 and the id step, then the URL rule where it is a URL —
 * relative ones too, whose query the free-text rules leave
 * (`/event/1/guests?guestName=Anna`).
 */
export function scrubSpanText(value: string): string {
  const s = scrubSelector(value);
  const call = METHOD_AND_URL.exec(s);
  if (call) return `${call[1] ?? ""} ${scrubUrl(call[2] ?? "")}`;
  return LONE_URL.test(s) ? scrubUrl(s) : s;
}

type CrumbLike = { category?: string; message?: string; data?: Record<string, unknown> };

/**
 * The keys under `breadcrumb.data` that carry a URL. `url` is the xhr/fetch
 * crumb; `from`/`to` are the NAVIGATION crumb, which records the SPA location
 * including its query string.
 */
const URL_CRUMB_KEYS: readonly string[] = ["url", "from", "to"];

/**
 * A console crumb, IN PLACE: `data.arguments` holds the logged objects
 * themselves — gone. The message becomes the first argument when that is a
 * string (the format text, not the values joined after it), else
 * `[console]`. A crumb without arguments keeps its message. Runs BEFORE
 * `pii-text` (which drops the arguments itself and would leave the joined
 * message).
 */
function consoleCrumbMessage(crumb: CrumbLike): void {
  const raw = crumb.data && typeof crumb.data === "object" ? crumb.data : undefined;
  if (crumb.category !== "console" || !raw || !("arguments" in raw)) return;
  const args = raw.arguments;
  delete raw.arguments;
  const first = Array.isArray(args) ? (args as unknown[])[0] : undefined;
  crumb.message = typeof first === "string" ? first : "[console]";
}

/**
 * One crumb, IN PLACE (the SDK keeps the object it handed the hook).
 * - console: see `consoleCrumbMessage`.
 * - ui.*: T8 and the id step on the element path.
 * - then `pii-text` (its free-text rules on message and data, the token
 *   path masked), and url / from / to by the URL rule.
 * Throws when the crumb cannot be read; the hooks fail closed around it.
 */
export function scrubBreadcrumb(crumb: CrumbLike): void {
  const category = typeof crumb.category === "string" ? crumb.category : "";
  consoleCrumbMessage(crumb);
  if (typeof crumb.message === "string" && category.startsWith("ui.")) {
    crumb.message = scrubSelector(crumb.message);
  }
  const out = scrubBreadcrumbText(crumb);
  if (typeof out.message === "string") crumb.message = out.message;
  const data = out.data;
  if (data && typeof data === "object") {
    // `pii-text` gives from / to the free-text rules only — a relative URL
    // keeps its query there (`/guests?guestName=Anna`).
    for (const key of URL_CRUMB_KEYS) {
      const v = data[key];
      if (typeof v === "string") data[key] = scrubUrl(v);
    }
    crumb.data = data;
  }
}

type SentryTransactionEvent = Parameters<NonNullable<BrowserOptions["beforeSendTransaction"]>>[0];
type SentrySpanJson = Parameters<NonNullable<BrowserOptions["beforeSendSpan"]>>[0];
type SentryIntegration = Extract<NonNullable<BrowserOptions["integrations"]>, readonly unknown[]>[number];
type SentryException = NonNullable<NonNullable<SentryEvent["exception"]>["values"]>[number];
type SentryStackFrame = NonNullable<NonNullable<SentryException["stacktrace"]>["frames"]>[number];
type SentryDebugImage = NonNullable<NonNullable<SentryEvent["debug_meta"]>["images"]>[number];

const URL_DATA_KEYS: ReadonlySet<string> = new Set(["url", "http.url", "url.full"]);

/** Span / trace `data` after `pii-text`: URL keys by the URL rule, every other string as a span text. */
function scrubSpanData(data: Record<string, unknown>): Record<string, unknown> {
  for (const [key, v] of Object.entries(data)) {
    if (typeof v !== "string") continue;
    data[key] = URL_DATA_KEYS.has(key) ? scrubUrl(v) : scrubSpanText(v);
  }
  return data;
}

/** The fields a minimal span keeps: ids, times and SDK-set kinds, never a name or data. */
const SAFE_SPAN_KEYS: readonly string[] = [
  "span_id",
  "trace_id",
  "parent_span_id",
  "start_timestamp",
  "timestamp",
  "op",
  "status",
  "origin",
  "segment_id",
  "is_segment",
  "exclusive_time",
];

/** Fail closed for a span whose scrub threw: ids and times, `[scrub-failed]`, no data. */
export function scrubFailedSpan(span: SentrySpanJson): SentrySpanJson {
  const out: Record<string, unknown> = { span_id: "", trace_id: "", start_timestamp: 0, data: {} };
  try {
    const source = span as unknown as Record<string, unknown>;
    for (const key of SAFE_SPAN_KEYS) {
      const v = source[key];
      if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") out[key] = v;
    }
  } catch {
    // unreadable — the defaults
  }
  out.description = SCRUB_FAILED;
  return out as unknown as SentrySpanJson;
}

function scrubSpanUnguarded(span: SentrySpanJson): SentrySpanJson {
  const scrubbed = (scrubEventValues({ spans: [span] }) as { spans: SentrySpanJson[] }).spans[0];
  const out = scrubbed ?? span;
  if (typeof out.description === "string") out.description = scrubSpanText(out.description);
  if (out.data) out.data = scrubSpanData(out.data);
  return out;
}

/**
 * Every span: child spans of a transaction, and the standalone ones (INP)
 * that pass no other hook. `pii-text`'s span rules first (they run through
 * its event scrub, which owns them), then T8 and the relative-URL rule.
 * Fails closed to `scrubFailedSpan`.
 */
export function sentryBeforeSendSpan(span: SentrySpanJson): SentrySpanJson {
  try {
    return scrubSpanUnguarded(span);
  } catch {
    return scrubFailedSpan(span);
  }
}

function scrubFrame(frame: SentryStackFrame): SentryStackFrame {
  const out = { ...frame };
  if (typeof out.filename === "string") out.filename = scrubFrameUrl(out.filename);
  if (typeof out.abs_path === "string") out.abs_path = scrubFrameUrl(out.abs_path);
  return out;
}

/** An exception's frames' files by the URL rule (its value had `pii-text`'s free-text rules). A copy. */
function scrubException(exception: SentryException): SentryException {
  const out = { ...exception };
  const frames = out.stacktrace?.frames;
  if (Array.isArray(frames)) out.stacktrace = { ...out.stacktrace, frames: frames.map(scrubFrame) };
  return out;
}

/** A debug image names its file like the frames do: the same rule keeps the two matching. */
function scrubDebugImage(image: SentryDebugImage): SentryDebugImage {
  const codeFile = (image as { code_file?: unknown }).code_file;
  return typeof codeFile === "string" ? ({ ...image, code_file: scrubFrameUrl(codeFile) } as SentryDebugImage) : image;
}

/**
 * Any event — error, transaction, feedback. `pii-text`'s event scrub first
 * (its free-text rules — U1, T, the token, e-mail, phone — on message /
 * logentry / exception values / extra / tags / contexts, U1 on request and
 * Referer, the span rules on transaction, spans and trace data; returns a
 * copy), then what only a browser produces: console crumbs (before it),
 * ui.* / span / extra / contexts element paths, relative URLs in crumbs,
 * spans and the transaction name, the feedback widget's page url, stack frame
 * files and debug images.
 * Throws when the event cannot be read; every caller fails closed around it.
 */
export function scrubEventEnvelope<E extends SentryEvent>(event: E): E {
  // Read before any free-text pass: that one cuts a URL at a quote
  // (`?guestName=O'Brien` would keep `'Brien`), the URL rule drops the query.
  const feedbackUrl = event.contexts?.feedback?.url;
  // Console crumbs first (on copies): `pii-text` drops the arguments itself.
  const input = Array.isArray(event.breadcrumbs)
    ? {
        ...event,
        breadcrumbs: event.breadcrumbs.map((crumb) => {
          const copy = { ...crumb, ...(crumb.data ? { data: { ...crumb.data } } : {}) };
          consoleCrumbMessage(copy);
          return copy;
        }),
      }
    : event;
  const out = scrubEventValues(input);
  if (Array.isArray(out.exception?.values)) {
    out.exception = { ...out.exception, values: out.exception.values.map(scrubException) };
  }
  if (Array.isArray(out.debug_meta?.images)) {
    out.debug_meta = { ...out.debug_meta, images: out.debug_meta.images.map(scrubDebugImage) };
  }
  if (typeof out.transaction === "string") out.transaction = scrubSpanText(out.transaction);
  // An element path in extra / contexts: browserApiErrors' Event target.
  if (out.extra) out.extra = deepMapStrings(out.extra, scrubSelector) as typeof out.extra;
  if (out.contexts) {
    const contexts = deepMapStrings(out.contexts, scrubSelector) as NonNullable<typeof out.contexts>;
    const trace = contexts.trace;
    if (trace?.data) trace.data = scrubSpanData(trace.data);
    const feedback = contexts.feedback;
    if (feedback && typeof feedbackUrl === "string") feedback.url = scrubUrl(feedbackUrl);
    out.contexts = contexts;
  }
  if (Array.isArray(out.breadcrumbs)) {
    for (const crumb of out.breadcrumbs) scrubBreadcrumb(crumb);
  }
  if (Array.isArray(out.spans)) out.spans = out.spans.map((span) => sentryBeforeSendSpan(span));
  return out;
}

/**
 * The transaction itself: name, request, trace data, child spans. Fails
 * closed: a transaction whose scrub threw is dropped (`failClosedEvent` gives
 * null for it — never a `[scrub-failed]` error in its place).
 */
export function sentryBeforeSendTransaction(event: SentryTransactionEvent): SentryTransactionEvent | null {
  try {
    return scrubEventEnvelope(event);
  } catch {
    return failClosedEvent(event);
  }
}

/**
 * `processEvent` runs for every event type — the only hook a feedback event
 * passes, and the one that reaches a transaction's request before anything
 * else reads it. Fails closed by type (`failClosedEvent`): an error becomes
 * the minimal `[scrub-failed]` event, a feedback a minimal feedback, a
 * transaction (or any other type) is dropped.
 */
export function sentryScrubIntegration(): SentryIntegration {
  return {
    name: "VeltricFreeTextScrub",
    processEvent(event) {
      try {
        return scrubEventEnvelope(event);
      } catch {
        return failClosedEvent(event);
      }
    },
  };
}
// ── canonical:end ───────────────────────────────────────────────────────────

/**
 * The connection heartbeat (`lib/connection/ConnectionWatcher`) reads this URL
 * every 10 s while the server is unreachable and every 60 s of idle otherwise.
 * Sentry's default breadcrumbs integration records one xhr crumb per request,
 * so an outage would fill the 100-crumb ring with heartbeat within ~17 min and
 * push out the crumbs that explain the error. Matched on the URL, so the app
 * bar's one-off read of the same count goes too — same request, nothing to
 * learn from it. Only request crumbs: a console line quoting the path stays.
 */
const HEARTBEAT_URL_PART = "/user-logs/num-unread";
/**
 * The new-version poll (lib/version/build-id.ts) — every 5 minutes and on
 * every focus, per tab. Same reasoning as the heartbeat: nothing to learn,
 * and it would crowd real requests out of the 100-crumb buffer.
 */
const VERSION_POLL_URL_PART = "/version.json";

function isHeartbeatCrumb(crumb: { category?: string; data?: Record<string, unknown> }): boolean {
  if (crumb.category !== "xhr" && crumb.category !== "fetch") return false;
  const url = crumb.data?.url;
  return (
    typeof url === "string" &&
    (url.includes(HEARTBEAT_URL_PART) || url.includes(VERSION_POLL_URL_PART))
  );
}

/**
 * Record-time twin of the scrub in `sentryBeforeSend`: a crumb scrubbed when it
 * is recorded never sits in the SDK's buffer with a value at all, so anything
 * that reads the buffer besides an error event (a transaction, a feedback
 * envelope — neither passes through `beforeSend`) is clean too. Returns null —
 * "drop it" to the SDK — for the heartbeat's own crumbs.
 */
export function sentryBeforeBreadcrumb<T extends CrumbLike>(crumb: T): T | null {
  try {
    if (isHeartbeatCrumb(crumb)) return null;
    scrubBreadcrumb(crumb);
    return crumb;
  } catch {
    // Fail closed (K4-B): a crumb whose scrub threw is dropped, never kept as
    // it was — and the throw never reaches the app (this runs inside the
    // instrumented console.* / fetch / click handler).
    return null;
  }
}

/**
 * Fails closed to `failClosedEvent`: a scrub that throws never sends the
 * original. Its `null` is for non-error types only, which this hook never gets.
 */
export function sentryBeforeSend(event: SentryErrorEvent): SentryErrorEvent | null {
  try {
    return scrubErrorEvent(event);
  } catch {
    return failClosedEvent(event);
  }
}

function scrubErrorEvent(event: SentryErrorEvent): SentryErrorEvent {
  // user.email / user.username / user.ip_address all carry PII — keep only id.
  if (event.user) {
    const { id } = event.user;
    event.user = id ? { id } : undefined;
  }

  if (event.request) {
    event.request.headers = scrubHeaders(
      event.request.headers as Record<string, string> | undefined,
    );
    if (event.request.data && typeof event.request.data === "object") {
      event.request.data = scrubPii(event.request.data);
    }
  }

  if (event.extra) event.extra = scrubPii(event.extra);
  if (event.contexts) {
    event.contexts = scrubPii(event.contexts) as typeof event.contexts;
  }

  // The free text and the URLs (Kanzlei K4-B) — `sentryScrubIntegration`
  // already did this for every event type; again here for an error that
  // reaches the hook some other way. Backend prose reaches the exception value
  // (translateBackendError, ErrorHandler.reportIfOurs) and carries field
  // values ("Value 'Erdnuss' is not a valid option"); breadcrumbs carry every
  // XHR with its full url, and this product puts the operator's SEARCH TERM
  // there (`?search=<name or email>` on the guest, sent-mails and check-in
  // lanes). Returns a scrubbed copy.
  return scrubEventEnvelope(event);
}

/**
 * What `main.tsx` hands `Sentry.init`, besides `sentryScrubIntegration()` in
 * its integration list: the four hooks and the text length the SDK cuts at.
 * One object so a hook cannot be added here and forgotten there; pinned by
 * `sentry.hooks.test.ts`, the wiring in `main.tsx` by `sentry.wiring.test.ts`.
 */
export function sentryScrubOptions() {
  return {
    maxValueLength: SENTRY_MAX_VALUE_LENGTH,
    beforeSend: sentryBeforeSend,
    beforeBreadcrumb: sentryBeforeBreadcrumb,
    beforeSendTransaction: sentryBeforeSendTransaction,
    beforeSendSpan: sentryBeforeSendSpan,
  } satisfies Partial<BrowserOptions>;
}
